// Shared station icon registration + snapshot helpers for MapLibre maps.
import { registerStationIcons } from '../_stationMarkerIcons'

const SNAPSHOT_BASE = 'https://d1nezfegjr0s5p.cloudfront.net/map-status/v1'

// --- Snapshot helpers ---

export function snapshotURL(date) {
  const d = new Date(date)
  const minutes = Math.floor(d.getUTCMinutes() / 5) * 5
  d.setUTCMinutes(minutes, 0, 0)
  const yyyy = d.getUTCFullYear()
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(d.getUTCDate()).padStart(2, '0')
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const mi = String(d.getUTCMinutes()).padStart(2, '0')
  return `${SNAPSHOT_BASE}/${yyyy}-${mm}-${dd}/${hh}${mi}.json.gz`
}

export async function fetchSnapshot() {
  const url = snapshotURL(new Date())
  const resp = await fetch(url)
  if (!resp.ok) return null
  return resp.json()
}

// --- Icon rendering ---

// The station markers are drawn by the VENDORED spec of record — one copy for
// the explore map and the station pages, where there used to be two
// byte-identical ones (this module and an inline block inside ExploreMap's
// doImperativeInit) that differed only in a constant's name. Two copies of a
// drawing is two designs waiting to diverge.
//
// The set is registered at pixelRatio 2. The markers this replaced were a 1x
// 24 px bitmap registered with no pixelRatio at all, so every retina device
// resampled them — a defect, not a style choice.
export function addStationIcons(map) {
  return registerStationIcons(map)
}

// ---- L2 labels: `<State> <value>` -----------------------------------------
//
// The state word costs nothing on the wire: the marker code already knows it,
// because it is what picks the sprite (r/f/high/low for tides, the snapshot's
// e/f/s for currents). Saying it in words makes the glyph CONFIRMABLE rather
// than load-bearing — the chevron and the arrow stop being the only place the
// state lives.
//
// Localized, which the spec's English examples are not: this site ships 12
// languages and the state words already exist as catalog keys. "Rising 5.1 ft"
// is the English rendering of the contract, not the contract itself.
//
// The zoom rule lives in the STYLE, not here — see the text-field step on the
// marker layers. Below z10 markers are dots that collide hard, so a longer
// string just loses the collision and shows nothing; both strings ride on the
// feature and the style picks one.
export function tideMapLabel(t, fmt, status, cm) {
  const word = { r: t('rising'), f: t('falling'), h: t('high'), l: t('low') }[status] || ''
  const value = fmt.mapHeight(cm)
  return word ? `${word} ${value}` : value
}

// fmt is unused for currents (knots are knots in both unit systems) and is kept
// in the signature so the two label helpers read the same at every call site.
export function currentMapLabel(t, fmt, status, knots) {
  if (status === 's' || !(knots >= 0.05)) return t('slack')
  const word = { f: t('flood'), e: t('ebb') }[status] || ''
  const value = t('map_knots_label', { speed: knots.toFixed(1) })
  return word ? `${word} ${value}` : value
}

// --- Status labels ---

export function tideStatusLabel(t, status) {
  switch (status) {
    case 'r':
      return t('rising')
    case 'f':
      return t('falling')
    case 'h':
      return t('high_tide')
    case 'l':
      return t('low_tide')
    default:
      return ''
  }
}

export function currentStatusLabel(t, status) {
  switch (status) {
    case 'e':
      return t('ebb')
    case 'f':
      return t('flood')
    case 's':
      return t('slack')
    default:
      return ''
  }
}

export function degreesToCompass(deg) {
  const dirs = [
    'N',
    'NNE',
    'NE',
    'ENE',
    'E',
    'ESE',
    'SE',
    'SSE',
    'S',
    'SSW',
    'SW',
    'WSW',
    'W',
    'WNW',
    'NW',
    'NNW',
  ]
  return dirs[Math.round(deg / 22.5) % 16]
}
